// HTTP client for the CUSTOMER portal's password-session endpoints (ADR-179
// P1: services/console/server/session_customer.go). Same plain-HTTP-not-gRPC
// rationale as the operator's auth-http.ts (Set-Cookie/CSRF are HTTP
// concerns) — these routes are PATH-NAMESPACED under /customer/auth/* on
// the same 8130 fallback mux, distinct from the operator's /auth/*.
//
// Every call uses credentials: 'include' for the same same-origin
// Set-Cookie/send-cookie reasons as the operator module.

// CustomerLoginSuccess mirrors session_customer.go's customerLoginResponse
// JSON body exactly.
export interface CustomerLoginSuccess {
  csrfToken: string
  userId: string
  accountId: string
  email: string
  role: string
}

// CustomerLoginPickerCandidate mirrors session_customer.go's
// customerLoginPickerCandidate — one row of a multi-account picker
// (ADR-179 P4).
export interface CustomerLoginPickerCandidate {
  accountId: string
  accountName: string
  role: string
}

// CustomerLoginPicker mirrors session_customer.go's
// customerLoginPickerResponse — returned by /customer/auth/login when the
// email verified against MORE THAN ONE account's password. No session
// exists yet; the caller must POST pickerToken + the chosen accountId to
// /customer/auth/login/select.
export interface CustomerLoginPicker {
  picker: CustomerLoginPickerCandidate[]
  pickerToken: string
}

// CustomerLoginResult is login()'s return type: either a minted session
// (kind 'session') or an account picker (kind 'picker') — ADR-179 P4's two
// possible OUTCOMES of a 200 response now that the same email can verify
// against more than one account. A 401 remains a thrown CustomerLoginError,
// unchanged.
export type CustomerLoginResult =
  | { kind: 'session'; session: CustomerLoginSuccess }
  | { kind: 'picker'; picker: CustomerLoginPicker }

// LoginError distinguishes the generic invalid-credentials case (which the
// server returns identically for unknown email / wrong password / a
// disabled user / zero-of-N verified accounts — no enumeration oracle) from
// other failures. The UI shows one generic message regardless.
export class CustomerLoginError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'CustomerLoginError'
    this.status = status
  }
}

interface JsonErrorBody {
  error?: string
}

async function parseError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as JsonErrorBody
    if (body && typeof body.error === 'string' && body.error) return body.error
  } catch {
    // non-JSON body; fall through to a status-derived message
  }
  return `request failed (${res.status})`
}

interface LoginSessionBody {
  csrf_token: string
  user_id: string
  account_id: string
  email: string
  role: string
}

interface LoginPickerBody {
  picker: Array<{ account_id: string; account_name: string; role: string }>
  picker_token: string
}

function isPickerBody(body: LoginSessionBody | LoginPickerBody): body is LoginPickerBody {
  return Array.isArray((body as LoginPickerBody).picker)
}

// login POSTs {email, password} to /customer/auth/login (ADR-179 P4: the
// account_ref field was removed from the password form — the operator
// decision is "password login is EMAIL + PASSWORD only", auto-detecting
// which account(s) the email verifies against server-side; see
// session_customer.go's handleLogin doc for the full decision tree). On a
// single verified account the browser has already stored the HttpOnly
// customer_session cookie plus the customer_csrf cookie (Set-Cookie on the
// same response) and this resolves { kind: 'session' }. On more than one
// verified account, NO cookie is set yet and this resolves
// { kind: 'picker' } — the caller must call selectAccount with the chosen
// account_id.
export async function login(email: string, password: string): Promise<CustomerLoginResult> {
  const res = await fetch('/customer/auth/login', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  if (!res.ok) {
    throw new CustomerLoginError(await parseError(res), res.status)
  }
  const body = (await res.json()) as LoginSessionBody | LoginPickerBody
  if (isPickerBody(body)) {
    return {
      kind: 'picker',
      picker: {
        picker: body.picker.map((c) => ({
          accountId: c.account_id,
          accountName: c.account_name,
          role: c.role,
        })),
        pickerToken: body.picker_token,
      },
    }
  }
  return {
    kind: 'session',
    session: {
      csrfToken: body.csrf_token,
      userId: body.user_id,
      accountId: body.account_id,
      email: body.email,
      role: body.role,
    },
  }
}

// selectAccount POSTs {picker_token, account_id} to
// /customer/auth/login/select — the second step of the ADR-179 P4 picker
// flow, redeeming a picker_token from a prior login() picker result for one
// of the pinned accounts. On 200 the browser has stored the session cookies
// exactly as a direct login() would.
export async function selectAccount(pickerToken: string, accountId: string): Promise<CustomerLoginSuccess> {
  const res = await fetch('/customer/auth/login/select', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ picker_token: pickerToken, account_id: accountId }),
  })
  if (!res.ok) {
    throw new CustomerLoginError(await parseError(res), res.status)
  }
  const body = (await res.json()) as LoginSessionBody
  return {
    csrfToken: body.csrf_token,
    userId: body.user_id,
    accountId: body.account_id,
    email: body.email,
    role: body.role,
  }
}

// logout POSTs to /customer/auth/logout to revoke the server-side session
// row and clear the cookies. Idempotent and best-effort — same rationale as
// the operator module's logout: never trap the customer in the app on a
// network failure.
export async function logout(): Promise<void> {
  try {
    await fetch('/customer/auth/logout', { method: 'POST', credentials: 'include' })
  } catch {
    // Swallow — local teardown proceeds regardless.
  }
}

// changePassword POSTs {old_password,new_password} to
// /customer/auth/change-password. Session+CSRF gated; on success the server
// revokes ALL of this customer's sessions (including this one), so the
// caller must treat a successful change as a logout.
export async function changePassword(
  oldPassword: string,
  newPassword: string,
  csrfToken: string,
): Promise<void> {
  const res = await fetch('/customer/auth/change-password', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
  })
  if (!res.ok) {
    throw new Error(await parseError(res))
  }
}

// ssoStartUrl builds the OIDC/SAML "start" redirect URL for the given
// account_ref. This is a plain top-level navigation (window.location), not
// a fetch — both flows redirect the browser to the IdP. Left unauthenticated
// against whether the account actually HAS that SSO method configured: an
// account with no config simply 404s server-side (oidc_customer.go's
// handleStart / saml_customer.go's handleStart both return 404 "not
// configured for this account" — see those handlers) after the click,
// which the portal login page surfaces as a plain error rather than trying
// to pre-discover config client-side (no RPC exists for that today; ADR-179
// D4 does not add one).
export function ssoStartUrl(method: 'oidc' | 'saml', accountRef: string): string {
  return `/customer/auth/${method}/${encodeURIComponent(accountRef)}/start`
}

// friendlyError: strip the gRPC service prefix ("console: ", "billing: ")
// that grpc-web surfaces at the front of server error strings before
// showing them to a customer. Found in the 2026-08-31 browser walkthrough:
// a bogus invite token rendered "console: invite not found or already
// used" -- the prefix is transport detail, not something a customer
// should read. Applies repeatedly (a wrapped error can carry two).
export function friendlyError(err: unknown, fallback: string): string {
  const raw = err instanceof Error ? err.message : ''
  if (!raw) return fallback
  return raw.replace(/^([a-z][a-z0-9_-]*:\s+)+/i, '') || fallback
}
